import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function CoreFeatures() {
  const { t, i18n } = useTranslation();
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const features = [
    {
      titleKey: 'features.feature1.title',
      descriptionKey: 'features.feature1.description',
      image: i18n.language === 'zh-TW' 
        ? 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/15546cf5c5f3fc8b43645eef59da320a.png'
        : 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/db52e28eaf096c2f970cce61310db7a5.png',
      icon: 'ri-calendar-check-line',
      gradient: 'from-teal-500/20 to-emerald-500/20',
      iconBg: 'bg-teal-500/20',
      iconColor: 'text-teal-400'
    },
    {
      titleKey: 'features.feature2.title',
      descriptionKey: 'features.feature2.description',
      image: i18n.language === 'zh-TW'
        ? 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/9ca4639ebdd10f0e65d8d4c83a84e59a.png'
        : 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/23c69749b21b975b783af49ba9cfd365.jpeg',
      icon: 'ri-notification-badge-line',
      gradient: 'from-emerald-500/20 to-cyan-500/20',
      iconBg: 'bg-emerald-500/20',
      iconColor: 'text-emerald-400'
    },
    {
      titleKey: 'features.feature3.title',
      descriptionKey: 'features.feature3.description',
      image: i18n.language === 'zh-TW'
        ? 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/4f18e667e39a8ada39dd061e6e5de61f.jpeg'
        : 'https://static.readdy.ai/image/584ac40951726429431482b4d17e4edf/af10cca8518e184a64cf3b3719e53439.jpeg',
      icon: 'ri-moon-line',
      gradient: 'from-cyan-500/20 to-teal-500/20',
      iconBg: 'bg-cyan-500/20',
      iconColor: 'text-cyan-400'
    }
  ];

  return (
    <>
      <section id="features" className="relative py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 font-['Space_Grotesk']">
              {t('features.title')}
            </h2>
          </div>

          <div className="space-y-24">
            {features.map((feature, index) => (
              <div 
                key={index}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
              >
                {/* Content */}
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                  <div className={`w-16 h-16 flex items-center justify-center rounded-2xl ${feature.iconBg}`}>
                    <i className={`${feature.icon} text-3xl ${feature.iconColor}`}></i>
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-bold text-white font-['Space_Grotesk']">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="text-lg text-slate-300 leading-relaxed">
                    {t(feature.descriptionKey)}
                  </p>
                </div>

                {/* Image */}
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  {feature.image.startsWith('http') ? (
                    <div 
                      onClick={() => setSelectedImage(feature.image)}
                      className="relative aspect-video rounded-2xl overflow-hidden cursor-pointer hover:scale-105 transition-transform duration-300"
                    >
                      <img 
                        src={feature.image}
                        alt={t(feature.titleKey)}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                  ) : (
                    <div className={`relative aspect-video rounded-2xl bg-gradient-to-br ${feature.gradient} border border-slate-700/50 backdrop-blur-sm flex items-center justify-center overflow-hidden`}>
                      <div className="absolute inset-0 bg-slate-900/30"></div>
                      <div className="relative text-center space-y-4 p-8">
                        <div className="w-20 h-20 mx-auto flex items-center justify-center">
                          <i className="ri-image-line text-6xl text-slate-600"></i>
                        </div>
                        <p className="text-slate-500 font-medium">{feature.image}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          onClick={() => setSelectedImage(null)}
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4 cursor-pointer"
        >
          <button
            onClick={() => setSelectedImage(null)}
            className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-3xl"></i>
          </button>
          <img 
            src={selectedImage}
            alt="Full size view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}